import { useState, useCallback } from 'react'
import { ActiveFilter, FilterField, AppliedFilters } from '@features/file-explorer/components/Toolbar/filterTypes'
import { generateFilterId } from '@features/file-explorer/components/Toolbar/filterUtils'

interface UseFiltersProps {
  initialFilters?: AppliedFilters
  filterFields: FilterField[]
  onApplyFilters: (filters: AppliedFilters) => void
}

export function useFilters({ initialFilters = {}, filterFields, onApplyFilters }: UseFiltersProps) {
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(() => {
    // Convert initialFilters to ActiveFilter array
    return Object.entries(initialFilters).map(([key, value]) => {
      const field = filterFields.find(f => f.key === key)
      return {
        id: generateFilterId(),
        field: key,
        operator: 'contains' as const, // Default operator
        value: String(value),
        fieldLabel: field?.label || key,
        operatorLabel: 'contains',
        valueLabel: String(value)
      }
    })
  })

  const [inputValue, setInputValue] = useState('')

  // Convert active filters to backend format
  const convertFiltersToBackend = useCallback((filters: ActiveFilter[]): AppliedFilters => {
    const backendFilters: AppliedFilters = {}

    filters.forEach(filter => {
      // Build the filter key based on operator
      let filterKey = filter.field

      switch (filter.operator) {
        case 'contains':
          filterKey = `${filter.field}_cont`
          break
        case 'equals':
          filterKey = `${filter.field}_eq`
          break
        case 'not_equal':
          filterKey = `${filter.field}_neq`
          break
        case 'starts_with':
          filterKey = `${filter.field}_start`
          break
        case 'ends_with':
          filterKey = `${filter.field}_end`
          break
        case 'greater_than':
          filterKey = `${filter.field}_gt`
          break
        case 'less_than':
          filterKey = `${filter.field}_lt`
          break
        case 'before':
          filterKey = `${filter.field}_lt`
          break
        case 'after':
          filterKey = `${filter.field}_gt`
          break
      }

      backendFilters[filterKey] = filter.value
    })

    return backendFilters
  }, [])

  const addFilter = useCallback((filter: Omit<ActiveFilter, 'id'>) => {
    const newFilters = [...activeFilters, { ...filter, id: generateFilterId() }]
    setActiveFilters(newFilters)
    const backendFilters = convertFiltersToBackend(newFilters)
    onApplyFilters(backendFilters)
  }, [activeFilters, convertFiltersToBackend, onApplyFilters])

  const removeFilter = useCallback((filterId: string) => {
    const newFilters = activeFilters.filter(f => f.id !== filterId)
    setActiveFilters(newFilters)
    const backendFilters = convertFiltersToBackend(newFilters)
    onApplyFilters(backendFilters)
  }, [activeFilters, convertFiltersToBackend, onApplyFilters])

  const clearAllFilters = useCallback(() => {
    setActiveFilters([])
    setInputValue('')
  }, [])

  return {
    activeFilters,
    inputValue,
    setInputValue,
    addFilter,
    removeFilter,
    clearAllFilters
  }
}
